import React from 'react';

import { MenuItem, Select, Typography } from '@mui/material';
import styled, { css } from 'styled-components';

import type { TabBadgeColor } from '.';

// MUI's standard badge height
const BADGE_HEIGHT = 20;

// A tab's vertical padding, in spacing units
const TAB_PADDING_Y = 0.5;

// A superscripted badge rises half its height above its label, less the tab's
// own padding, so a container that clips its overflow cuts that much off
const badgeRoom = css`
  padding-top: calc(${BADGE_HEIGHT / 2}px - ${({ theme }) => theme.spacing(TAB_PADDING_Y)});
`;

interface DesktopSelectorProps {
  $orientation: 'vertical' | 'horizontal';
  $justifyContent: React.CSSProperties['justifyContent'];
  $alignItems: React.CSSProperties['alignItems'];
  $dense: boolean;
  $stackWhenNarrow: boolean;
  $reserveBadgeRoom: boolean;
}

const DesktopSelector = styled.div<DesktopSelectorProps>`
  display: flex;
  flex-direction: ${({ $orientation }) => ($orientation === 'vertical' ? 'column' : 'row')};
  align-items: ${({ $alignItems }) => $alignItems};
  justify-content: ${({ $justifyContent }) => $justifyContent};
  gap: ${({ theme, $orientation }) => ($orientation === 'vertical' ? theme.spacing(1) : 0)};
  padding-bottom: ${({ theme, $orientation, $dense }) =>
    $orientation === 'vertical' || $dense ? 0 : theme.spacing(4)};
  ${({ $reserveBadgeRoom }) => $reserveBadgeRoom && badgeRoom}
  ${({ theme, $stackWhenNarrow }) =>
    $stackWhenNarrow &&
    css`
      // Labels are kept on one line, so a row that outgrows its container stacks
      // whole tabs onto their own lines rather than breaking each label in two
      flex-wrap: wrap;
      row-gap: ${theme.spacing(1)};
      white-space: nowrap;
    `}
`;

interface TabProps {
  $orientation: 'vertical' | 'horizontal';
}

// Only add side margins for horizontal orientation
const Tab = styled.div<TabProps>`
  margin-left: ${({ theme, $orientation }) =>
    $orientation === 'horizontal' ? theme.spacing(2) : 0};
  margin-right: ${({ theme, $orientation }) =>
    $orientation === 'horizontal' ? theme.spacing(2) : 0};
`;

// An SVG has no content-based minimum, so a cramped row shrinks the icon first.
const tabIcon = css`
  > svg {
    flex-shrink: 0;
  }
`;

interface TabTextProps {
  $selected: boolean;
  $color: 'primary' | 'error' | 'success';
}

const TabText = styled(Typography)<TabTextProps>`
  && {
    &:hover {
      background-color: ${({ theme }) => theme.palette.backdrop.main};
      color: ${({ theme, $color }) => theme.palette[$color].dark};
    }
    border-radius: ${({ theme }) => theme.shape.borderRadius * 3}px;
    padding: ${({ theme }) => theme.spacing(TAB_PADDING_Y, 1.5)};
    color: ${({ $selected, theme, $color }) =>
      $selected ? theme.palette[$color].dark : theme.palette.gray.main};
    font-weight: 400;
    border-color: ${({ theme, $color }) => theme.palette[$color].dark};
    background-color: ${({ theme, $selected }) =>
      $selected ? theme.palette.backdrop.main : 'inherit'};
    cursor: pointer;
    display: flex;
    justify-content: center;
    align-items: center;
    gap: ${({ theme }) => theme.spacing(1)};
    align-items: center;
    ${tabIcon}
  }
`;

interface MobileSelectorProps {
  $justifyContent: React.CSSProperties['justifyContent'];
  $alignItems: React.CSSProperties['alignItems'];
  $dense: boolean;
}

const MobileSelector = styled.div<MobileSelectorProps>`
  width: 100%;
  display: flex;
  align-items: ${({ $alignItems }) => $alignItems};
  justify-content: ${({ $justifyContent }) => $justifyContent};
  padding-left: ${({ theme, $dense }) => ($dense ? 0 : theme.spacing(2))};
  padding-right: ${({ theme, $dense }) => ($dense ? 0 : theme.spacing(2))};
  padding-bottom: ${({ theme, $dense }) => ($dense ? 0 : theme.spacing(4))};
`;

// A tab row is navigation, so the selector it collapses into keeps the row's own
// flat, centred look rather than the outlined field an input select wears. Only
// the arrow is left to MUI, so every dropdown in the app drops the same one.
const Dropdown = styled(Select)`
  && {
    // A floor, not a fixed width: a long label widens the field, not overflows it.
    min-width: 240px;
    max-width: 100%;
    height: 40px;
    background-color: transparent;
    border: ${({ theme }) => `1.5px solid ${theme.palette.backdrop.main}`};
    border-radius: ${({ theme }) => theme.shape.borderRadius}px;
    text-align: center;
    font-weight: 600;
    color: ${({ theme }) => theme.palette.gray.main};
    & .MuiOutlinedInput-notchedOutline {
      border: none;
    }
    & .MuiSelect-select {
      display: flex;
      justify-content: center;
      align-items: center;
      gap: ${({ theme }) => theme.spacing(1)};
      ${tabIcon}
    }
  }
`;

const DropdownItem = styled(MenuItem)`
  && {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: ${({ theme }) => theme.spacing(1)};
    color: ${({ theme }) => theme.palette.gray.dark};
    ${tabIcon}
  }
`;

// A bare text node has no box for `.MuiSelect-select`'s ellipsis to act on.
const TabLabel = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

interface InlineBadgeProps {
  $color: TabBadgeColor;
}

// Sized from MUI's own standard badge, so a count reads the same whether it
// overlays a tab or sits beside a label
const InlineBadge = styled.span<InlineBadgeProps>`
  // The baseline has box-sizing inherit, and MUI's input styles set content-box
  // on the field, so the closed select would otherwise lay the padding outside
  // the minimum width and stretch a single digit into a pill
  box-sizing: border-box;
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: ${BADGE_HEIGHT}px;
  height: ${BADGE_HEIGHT}px;
  padding: ${({ theme }) => theme.spacing(0, 0.75)};
  border-radius: ${BADGE_HEIGHT / 2}px;
  background-color: ${({ theme, $color }) => theme.palette[$color].main};
  color: ${({ theme, $color }) => theme.palette[$color].contrastText};
  font-size: 0.75rem;
  font-weight: 500;
  line-height: 1;
`;

export {
  badgeRoom,
  DesktopSelector,
  Dropdown,
  DropdownItem,
  InlineBadge,
  Tab,
  MobileSelector,
  TabLabel,
  TabText,
};
